import { useState, useCallback, useEffect } from 'react';
import { calculateZoom } from './imageTransform';

interface UseZoomControlsProps {
  isZoomLocked: boolean;
  onTransformChange?: (scale: number, rotation: number) => void;
  onPositionChange?: (position: { x: number; y: number }) => void;
  currentPosition?: { x: number; y: number };
  containerRef?: React.RefObject<HTMLDivElement | null>;
}

export const useZoomControls = ({
  isZoomLocked,
  onTransformChange,
  onPositionChange,
  currentPosition = { x: 0, y: 0 },
  containerRef
}: UseZoomControlsProps) => {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [zoomInterval, setZoomInterval] = useState<NodeJS.Timeout | null>(null);

  // Function to adjust position to keep a specific point fixed during zoom
  const adjustPositionForZoom = useCallback((newScale: number, oldScale: number) => {
    // Current implementation uses transform-origin: center top, so position remains stable relative to the top
  }, []);

  const zoomIn = useCallback((e: React.MouseEvent | React.WheelEvent) => {
    if (isZoomLocked) return;
    e.stopPropagation();
    setScale(prev => {
      const next = calculateZoom(prev, 0.1, 0.2, 5);
      adjustPositionForZoom(next, prev);
      onTransformChange?.(next, rotation);
      return next;
    });
  }, [isZoomLocked, rotation, onTransformChange, adjustPositionForZoom]);

  const zoomOut = useCallback((e: React.MouseEvent | React.WheelEvent) => {
    if (isZoomLocked) return;
    e.stopPropagation();
    setScale(prev => {
      const next = calculateZoom(prev, -0.1, 0.2, 5);
      adjustPositionForZoom(next, prev);
      onTransformChange?.(next, rotation);
      return next;
    });
  }, [isZoomLocked, rotation, onTransformChange, adjustPositionForZoom]);

  const zoomInStart = useCallback((e: React.MouseEvent) => {
    if (isZoomLocked) return;
    e.stopPropagation();
    zoomIn(e);
    
    const interval = setInterval(() => {
      setScale(prev => {
        const next = calculateZoom(prev, 0.05, 0.2, 5);
        adjustPositionForZoom(next, prev);
        onTransformChange?.(next, rotation);
        return next;
      });
    }, 100);
    
    setZoomInterval(interval);
  }, [isZoomLocked, zoomIn, rotation, onTransformChange, adjustPositionForZoom]);

  const zoomOutStart = useCallback((e: React.MouseEvent) => {
    if (isZoomLocked) return;
    e.stopPropagation();
    zoomOut(e);
    
    const interval = setInterval(() => {
      setScale(prev => {
        const next = calculateZoom(prev, -0.05, 0.2, 5);
        adjustPositionForZoom(next, prev);
        onTransformChange?.(next, rotation);
        return next;
      });
    }, 100);
    
    setZoomInterval(interval);
  }, [isZoomLocked, zoomOut, rotation, onTransformChange, adjustPositionForZoom]);

  const zoomStop = useCallback(() => {
    if (zoomInterval) {
      clearInterval(zoomInterval);
      setZoomInterval(null);
    }
  }, [zoomInterval]);

  const resetZoom = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setScale(1);
    setRotation(0);
    onPositionChange?.({ x: 0, y: 0 });
    onTransformChange?.(1, 0);
  }, [onTransformChange, onPositionChange]);

  const rotateLeft = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const newRotation = rotation - 90;
    setRotation(newRotation);
    onTransformChange?.(scale, newRotation);
  }, [rotation, scale, onTransformChange]);

  const rotateRight = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const newRotation = rotation + 90;
    setRotation(newRotation);
    onTransformChange?.(scale, newRotation);
  }, [rotation, scale, onTransformChange]);

  const toggleZoom = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (scale > 1) {
      resetZoom(e);
    } else {
      const newScale = 2;
      setScale(newScale);
      adjustPositionForZoom(newScale, scale);
      onTransformChange?.(newScale, rotation);
    }
  }, [scale, rotation, resetZoom, onTransformChange, adjustPositionForZoom]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (zoomInterval) {
        clearInterval(zoomInterval);
      }
    };
  }, [zoomInterval]);

  return {
    scale,
    rotation,
    zoomIn,
    zoomOut,
    zoomInStart,
    zoomOutStart,
    zoomStop,
    resetZoom,
    rotateLeft,
    rotateRight,
    toggleZoom,
    setScale,
    setRotation,
  };
}; 